import { BaseModel } from "./BaseModel";
import { ColumnHeaders, TabNames, Currency } from '../modules/Constants';

export class MECapitalModel extends BaseModel {
    supplementalFormatting(formatting) {
        
        formatting.columns[4].width = 300;
        formatting.columns[5].width = 300;
        formatting.columns[6].width = 150;
        formatting.columns[formatting.columns.length - 8].width = 150;
        formatting.columns[formatting.columns.length - 1].width = 300;

        return formatting;
    }

    constructor() {
        super();
        this.TabName = TabNames.ME_CAPITAL;
        this.TabIndex = 6;
        this.DataProcessor = new MECapitalProcessor();
    }

}

export class MECapitalProcessor {
    ExchangeRates;
    Years;
    Currency;
    Records;
    Headers;

    process(data) {

        this.ExchangeRates = [];
        this.Years = [];
        this.Records = [];
        this.Headers = [];
        this.Currency = data.currency;

        let study = data.study;
        let snapshots = data.snapshots;
        let version = data.version;


        study.exchangeRates.forEach(rate => {
            rate.Item2.Values.forEach(item => {
                this.ExchangeRates.push({
                    currency: rate.Item1,
                    year: new Date(item.Date).getUTCFullYear(),
                    value: item.Value
                });
            });
        });


        let firstSOPYear = Math.min.apply(Math, snapshots.map(snapshot => snapshot.SaleableParts?.map(part => new Date(part?.CustomerSOP?.Value.Fields[0]).getUTCFullYear())).reduce(function (a, b) { return a.concat(b) }, []));
        let firstYearSpend = firstSOPYear - (version?.SpendYearsPriorToSOP?.Value?.Fields[0] ?? 2);
        let lastYearSpend = firstSOPYear + (version?.SpendYearsAfterSOP?.Value?.Fields[0] ?? 0);
        snapshots.forEach(snapshot => {
            snapshot.SaleableParts.forEach(part => {
                let endDate = part.CustomerVolume?.Value?.Fields[0]?.EndDate;
                if (endDate) {
                    let year = new Date(endDate).getUTCFullYear();
                    if (year > lastYearSpend) {
                        lastYearSpend = year;
                    }
                }
            });
        })

        var yearsSpending = Array.from(Array((lastYearSpend - firstYearSpend) + 1), (_, x) => x + firstYearSpend);
        this.Years = yearsSpending;


        //JG - Update Years in case there are additional years outside of Spending Years.
        snapshots.forEach(snapshot => {
            snapshot?.Manufacturing.forEach(manufacturing => {
                manufacturing.Operations.forEach(op => {
                    let spendTiming = op.SpendTiming;

                    let spendTimingYears = spendTiming?.Value?.Fields[0]?.Values;

                    spendTimingYears.forEach(spendTimingYear => {
                        let year = new Date(spendTimingYear.Date).getUTCFullYear();

                        if (this.Years.findIndex(x => x === year) <= -1) {
                            this.Years.push(year);
                        }
                    });
                });
            });
        });

        this.Years.sort();

        snapshots.forEach(snapshot => {
            this.setRecords(snapshot);
        });

        this.configureHeader();
        let result = {
            headers: this.Headers,
            display: this.getDisplay(),
            numerics: this.getNumerics()
        };

        return result;
    }

    setRecords(snapshot) {
        snapshot?.Manufacturing.forEach(manufacturing => {
            manufacturing.Operations.forEach(op => {
                if (op.ManufacturingCapitalInvestment?.length < 1) {
                    this.Records.push(new MECapitalRowModel(this.Years, snapshot, manufacturing, op, null, this.ExchangeRates, this.Currency));
                    return;
                }

                op.ManufacturingCapitalInvestment.forEach(mfgCapitalInvestment => {
                    this.Records.push(new MECapitalRowModel(this.Years, snapshot, manufacturing, op, mfgCapitalInvestment, this.ExchangeRates, this.Currency));
                })
            })
        });
    }

    configureHeader() {
        let header = [
            ColumnHeaders.OPTION_NAME,
            ColumnHeaders.PLANT,
            ColumnHeaders.MFG_PRO_NAME,
            ColumnHeaders.OPERATION_NUMBER,
            ColumnHeaders.OPERATION_DESCRIPTION,
            ColumnHeaders.CAPITAL_INVESTMENT_DESCRIPTION,
            ColumnHeaders.CAPITAL_INVESTMENT_QTY,
            ColumnHeaders.CAPITAL_INVESTMENT_PRORATE_ELIGIBLE_PERCENT
        ];

        this.Years.forEach(year => {
            header.push(year + " Cost");
        });

        header.push(
            ColumnHeaders.START_YEAR,
            ColumnHeaders.CURRENCY_PREFIX
        )

        this.Years.forEach(year => {
            header.push(`${year} Cost ${ColumnHeaders.CONVERTED_PREFIX}`);
        });

        header.push(
            ColumnHeaders.TOTAL_CAPITAL_INVESTMENT_COST,
            ColumnHeaders.START_YEAR,
            ColumnHeaders.CURRENCY_PREFIX,
            ColumnHeaders.CAPITAL_INVESTMENT_RETOOL_TAG_NUMBER,
            ColumnHeaders.CAPITAL_INVESTMENT_FLOOR_SPACE_SQ_M,
            ColumnHeaders.CAPITAL_INVERSTMENT_POWER_CONSUMPTION,
            ColumnHeaders.LEAD_TIME_WEEKS,
            ColumnHeaders.MFG_COMMENTS
        )

        this.Headers = header;
    }

    getDisplay() {
        let display = [];

        display.push(this.Headers);

        this.Records.forEach(record => {
            let row = [
                record.OptionName,
                record.Plant,
                record.MfgProName,
                record.OperationNumber,
                record.OperationDescription,
                record.CapitalInvestmentDescription,
                record.CapitalInvestmentQuantity,
                record.CapitalInvestmentProrateEligiblePercent
            ];

            record.Cost.forEach(cost => {
                row.push(cost);
            });

            row.push(
                this.Years[0],
                record.Currency
            )

            record.CostConverted.forEach(cost => {
                row.push(cost);
            });

            var total = record.CostConvertedTotal;

            row.push(
                Number.isNaN(total) ? "" : total, //Total Cost (Converted)
                this.Years[0],
                this.Currency, // Currency (Converted)
                record.CapitalInvestmentRetoolTagNumber,
                record.CapitalInvestmentFloorSpaceSqM,
                record.CapitalInvestmentPowerConsumption,
                record.LeadTimeWeeks,
                record.MfgComments
            )


            display.push(row);
        });

        return display;
    }

    getNumerics() {
        let numeric = [];

        //set number formatting
        let startCost = 8;
        let endCost = this.Years.length + startCost - 1;
        let startConvCost = endCost + 3;
        let endConvCost = this.Years.length + startConvCost;

        numeric.push({ start: startCost, end: endCost });
        numeric.push({ start: startConvCost, end: endConvCost });

        return numeric;
    }
}

export class MECapitalRowModel {
    OptionName = "";
    Plant = "";
    MfgProName = "";
    OperationNumber = "";
    OperationDescription = "";
    CapitalInvestmentDescription = "";
    CapitalInvestmentQuantity = "";
    CapitalInvestmentProrateEligiblePercent = "";
    CapitalInvestmentRetoolTagNumber = "";
    CapitalInvestmentFloorSpaceSqM = "";
    CapitalInvestmentPowerConsumption = "";
    LeadTimeWeeks = "";
    MfgComments = "";
    Cost = [];
    CostConverted = [];
    CostConvertedTotal = 0;
    Currency = "";

    constructor(years, snapshot, manufacturing, operation, mfgCapitalInvestment, exchangeRates, exchangeCurreny) {

        this.OptionName = snapshot.GroupName;
        this.Plant = manufacturing?.Plant?.Value?.Fields[0]?.Number?.Fields[0];
        this.MfgProName = manufacturing?.Name?.Value?.Fields[0];
        this.OperationNumber = operation?.OperationNumber?.Fields[0];
        this.OperationDescription = operation?.Description?.Value?.Fields[0];
        this.CapitalInvestmentDescription = mfgCapitalInvestment?.Description?.Value?.Fields[0];
        this.CapitalInvestmentQuantity = mfgCapitalInvestment?.Quantity?.Value?.Fields[0];
        this.CapitalInvestmentRetoolTagNumber = mfgCapitalInvestment?.RetoolTagNumber?.Value?.Fields[0];
        this.CapitalInvestmentFloorSpaceSqM = mfgCapitalInvestment?.FloorSpace?.Value?.Fields[0];
        this.CapitalInvestmentPowerConsumption = mfgCapitalInvestment?.PowerConsumption?.Value?.Fields[0];
        this.MfgComments = operation?.MEComments?.Value?.Fields[0];
        this.LeadTimeWeeks = operation?.LeadTime?.Value?.Fields[0];
        this.Cost = [];
        this.CostConverted = [];
        this.CostConvertedTotal = 0;
        this.Currency = mfgCapitalInvestment?.Cost?.Value?.Fields[0].Fields[0]?.Tag;

        if (this.Currency === '') {
            this.Currency = Currency.USD;
        }

        let totalCost = 0;
        let totalProratableCost = 0;

        let spendTiming = operation?.SpendTiming?.Value?.Fields[0]?.Values;

        years.forEach(year => {
            let cost = undefined;
            let proratableCost = undefined;
            let convCost = undefined;

            if (mfgCapitalInvestment) {
                let spendTimingYear = spendTiming.find(x => new Date(x.Date).getUTCFullYear() === year);
                let mfgCapitalTotalCostValues = mfgCapitalInvestment?.Cost?.Value?.Fields[0]?.Fields[0]?.Values?.map(item => item.Value);
                let mfgCapitalTotalCost = mfgCapitalTotalCostValues?.length > 0 ? mfgCapitalTotalCostValues?.reduce((prev, next) => prev + next) : 0;
                cost = mfgCapitalTotalCost * (spendTimingYear?.Value ?? 0);
                let proratableTotalCostValues = mfgCapitalInvestment?.ProrateableInvestmentTotalCost?.Values?.map(item => item.Value);
                let proratableTotalCost = proratableTotalCostValues?.length > 0 ? proratableTotalCostValues?.reduce((prev, next) => prev + next) : 0;
                proratableCost = proratableTotalCost * (spendTimingYear?.Value ?? 0);

                let exchangeRate = exchangeRates?.find(rate => rate.year === year && rate.currency === exchangeCurreny);
                let currentCurrencyRate = exchangeRates?.find(rate => rate.year === year && rate.currency === this.Currency);

                if (exchangeRate === undefined || cost === undefined || currentCurrencyRate === undefined) {
                    if (cost !== undefined) {
                        this.CostConverted.push("Error");
                    } else {
                        this.CostConverted.push("");
                    }
                } else {
                    convCost = cost * exchangeRate.value / currentCurrencyRate.value;
                    this.CostConvertedTotal += convCost;
                }
            }

            totalCost += (Number.isNaN(cost) ? 0 : cost);
            totalProratableCost += (Number.isNaN(proratableCost) ? 0 : proratableCost);

            this.Cost.push(cost);
            this.CostConverted.push(convCost);
        });


        if (totalCost != 0) {
            this.CapitalInvestmentProrateEligiblePercent = (totalProratableCost / (totalCost));
        }

        if (Number.isNaN(this.CapitalInvestmentProrateEligiblePercent)) {
            this.CapitalInvestmentProrateEligiblePercent = "";
        }

        this.CostConvertedTotal = this.CostConvertedTotal * this.CapitalInvestmentQuantity;

    }
}